import React, { useEffect, useRef, useState } from "react";
import { ProgramCardProps } from "@shared/interfaces/ProgramFiles";
import { ProgramStatus } from "@pages/Program/utils/enums";
import { HiDotsVertical } from "react-icons/hi";
import { useUser } from "@services/UserContext";
import Pill from "@shared/components/Pill";

const ProgramCard: React.FC<ProgramCardProps> = ({
  id,
  name,
  icon,
  status,
  description,
  budget_spent,
  budget,
  creator_id,
  completion_percentage,
  onClick,
  handleDelete,
  handleEdit,
}) => {
  const [openPopup, setOpenPopup] = useState(false);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const { user } = useUser();

  const togglePopup = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenPopup((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node)
      ) {
        setOpenPopup(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div
      onClick={onClick}
      className="border border-gray-300 bg-gray-50 rounded-lg p-4 w-full relative shadow-sm flex flex-col justify-between cursor-pointer hover:bg-gray-100 hover:shadow transition duration-200 ease-in-out"
    >
      <div className="">
        <div className="absolute top-3 right-2 flex space-x-2 items-center">
          <Pill
            styling={`${
              status === "active"
                ? "bg-blue-100 text-blue-700"
                : "bg-gray-200 text-gray-500"
            }`}
            text={ProgramStatus[status]}
          />
          {user && creator_id == parseInt(user.id) && (
            <div className="relative">
              <button
                onClick={togglePopup}
                className="text-gray-500 hover:text-gray-700 p-1 border border-transparent rounded-full focus:outline-none "
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="2xl:h-5 2xl:w-5 h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="#0357C9"
                >
                  <path d="M10 4a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0zm0 6a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </button>

              {openPopup && (
                <div
                  ref={popupRef}
                  className="absolute right-0 mt-2 w-28 bg-white border border-gray-200 rounded-md shadow-lg z-20"
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(id);
                    }}
                    className="block w-full text-left px-4 py-2 text-xs 2xl:text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Edit
                  </button>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(id);
                    }}
                    className="block w-full text-left px-4 py-2 text-xs 2xl:text-sm text-red-600 hover:bg-gray-100"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center mb-1">
          <span className="w-6 h-6">{icon ? icon : "⭐"}</span>
          <h2 className="font-semibold text-sm 2xl:text-base">{name}</h2>
        </div>

        <p className="text-[0.67rem] 2xl:text-xs text-gray-600 mb-4 line-clamp-2">
          {description}
        </p>
      </div>

      <div className="flex items-start space-x-6">
        <div className="justify-between text-[0.67rem] 2xl:text-xs text-gray-600">
          <p className="text-[#98A2B3]">Budget</p>
          <p className="text-xs 2xl:text-sm text-black">
            {Number(budget_spent).toFixed(0)}$ /{" "}
            <span className="text-[#98A2B3]">{Number(budget).toFixed(0)}$</span>
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[0.67rem] 2xl:text-xs text-[#98A2B3]">
            Completion
          </p>
          <div className="w-full ml-auto flex items-center space-x-4">
            <div className="relative w-full h-1 bg-gray-200 rounded flex-1">
              <div
                className="absolute top-0 left-0 h-full bg-blue-500 rounded"
                style={{ width: `${completion_percentage}%` }}
              />
            </div>
            <p className="text-right text-[0.67rem] 2xl:text-xs font-semibold text-gray-800 self-center">
              {completion_percentage ?? completion_percentage}%
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramCard;
